import { Injectable, QueryList, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { ShoppingCartLine } from '../generic/classes/shoppingcartline';
import { AddBarcodeRowComponent } from '../components/my/order/pending/add.barcode.row.component';
import { Message } from '../generic/classes/message';
import { Logger } from '../generic/logging/default-log.service';
import { BaseService } from '../generic/service/base.service';
import { HttpClient } from '../generic/service/http.service';
import { ApplicationService } from '../generic/service/application.service';
import { ConfigurationHelper } from '../generic/helper/configuration.helper';

/**
 * Service for barcode related functionality
 * @author evandongen
 */
@Injectable()
export class BarcodeService extends BaseService {
    private http = inject(HttpClient);
    private applicationService = inject(ApplicationService);

    constructor() {
        const logger = inject(Logger);

        super(logger);
    }

    addBarcodes(
        lines: Array<ShoppingCartLine>,
        confirmedMessage: string,
        errorMessage: string,
    ) {
        const filteredLines = lines.filter(line => line.bcStatus === 1);

        filteredLines.forEach(line =>
            this.updateCustomerFieldsWithDefaultSuggestions(line),
        );

        const url = ConfigurationHelper.getWebshopUrl('/rest/barcode');

        const promise = lastValueFrom(
            this.http.post(url, JSON.stringify(filteredLines)),
        );

        promise.then(result =>
            this.showMessage(
                new Message(confirmedMessage, 'confirmation', false),
            ),
        );

        promise.catch(error =>
            this.showMessage(new Message(errorMessage, 'error', false)),
        );

        return promise;
    }

    /**
     * Updates the customer set barcode fields in the shopping cart line with the default
     * suggestions if the user didn't provide anything but submitted the form anyway.
     *
     * @param {ShoppingCartLine} shoppingCartLine
     */
    private updateCustomerFieldsWithDefaultSuggestions(
        shoppingCartLine: ShoppingCartLine,
    ) {
        if (!shoppingCartLine.scBarcode) {
            shoppingCartLine.scBarcode = shoppingCartLine.bcBarcode;
        }

        if (!shoppingCartLine.scBarcodeDescription) {
            shoppingCartLine.scBarcodeDescription =
                shoppingCartLine.bcBarcodeDescription;
        }

        if (!shoppingCartLine.scBarcodePrice) {
            shoppingCartLine.scBarcodePrice = shoppingCartLine.bcBarcodePrice;
        }
    }

    /**
     * Shorthand to save a single barcode
     *
     * @param {ShoppingCartLine} shoppingCartLine
     * @param {string} barcodeSuccess
     * @param {string} barcodeError
     */
    addBarcode(shoppingCartLine: ShoppingCartLine) {
        const url = ConfigurationHelper.getWebshopUrl('/rest/barcode');

        return lastValueFrom(
            this.http.post(url, JSON.stringify(new Array(shoppingCartLine))),
        );
    }

    /**
     * Update Barcode Sales Price by factor and rounding.
     * Only barcodes that have not been disabled or confirmed are allowed to
     * be updated.
     *
     * @param {QueryList} barcodeRows
     * @param {number} factor
     * @param {number} rounding
     */
    updateBarcodeSalesPrice(
        barcodeRows: QueryList<AddBarcodeRowComponent>,
        factor,
        rounding,
    ) {
        barcodeRows
            .filter(row => !row.barcodeDisabled && !row.confirmed)
            .forEach(child => {
                child.reCalculatePrice(+factor, +rounding);
            });
    }
    private showMessage(message: Message) {
        this.applicationService.message = message;
    }
}
